/**
 * iSTAT → VCM client/case matching logic.
 * Scores existing clients against incoming iSTAT data using parcel ID,
 * name, phone, and address. Returns ranked match results.
 */

import type { TcrClient, TcrCase, IstatMatchResult, IstatDiscrepancy } from '../../types/vcm';

interface IstatMatchInput {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  county: string;
  zip: string;
  parcelId?: string | undefined;
  damageLevel: string;
  waterDepth: string;
  ownerOrRenter: string;
  insuranceStatus: string;
}

function digits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().trim();
}

/**
 * Match an incoming iSTAT record against all VCM clients.
 * Returns scored results sorted by confidence (highest first).
 */
export function matchIstatToClients(
  input: IstatMatchInput,
  clients: TcrClient[],
  cases: TcrCase[],
): IstatMatchResult[] {
  const results: IstatMatchResult[] = [];
  const inputPhone = digits(input.phone);
  const inputFirst = normalize(input.firstName);
  const inputLast = normalize(input.lastName);
  const inputAddr = normalize(input.address);
  const inputEmail = normalize(input.email);

  for (const client of clients) {
    let score = 0;
    const reasons: string[] = [];
    const missed: string[] = [];

    // Name match (first + last)
    const clientFirst = normalize(client.firstName);
    const clientLast = normalize(client.lastName);
    if (inputLast && clientLast === inputLast && inputFirst && clientFirst === inputFirst) {
      score += 30;
      reasons.push('Name matches');
    } else if (inputLast && clientLast === inputLast) {
      score += 10;
      reasons.push('Last name matches');
      if (inputFirst) missed.push(`First name: "${input.firstName}" vs "${client.firstName}"`);
    } else {
      if (inputLast) missed.push(`Name: "${input.firstName} ${input.lastName}" vs "${client.firstName} ${client.lastName}"`);
    }

    // Phone match (strong signal)
    const clientPhone = digits(client.phoneHome);
    if (inputPhone.length >= 7 && clientPhone.length >= 7 && inputPhone === clientPhone) {
      score += 35;
      reasons.push('Phone matches');
    } else if (inputPhone.length >= 7 && clientPhone.length >= 7) {
      missed.push(`Phone: "${input.phone}" vs "${client.phoneHome ?? 'none'}"`);
    }

    // Email match
    if (inputEmail && normalize(client.email) === inputEmail) {
      score += 25;
      reasons.push('Email matches');
    } else if (inputEmail && client.email) {
      missed.push(`Email: "${input.email}" vs "${client.email}"`);
    }

    // Address match
    const clientAddr = normalize(client.preDisasterAddress?.address1);
    if (inputAddr.length >= 5 && clientAddr.length >= 5 && clientAddr.includes(inputAddr)) {
      score += 20;
      reasons.push('Address matches');
    } else if (inputAddr.length >= 5 && clientAddr.length >= 5) {
      missed.push(`Address: "${input.address}" vs "${client.preDisasterAddress?.address1 ?? 'none'}"`);
    }

    // County match (weak — only a tiebreaker)
    if (normalize(input.county) === normalize(client.county)) {
      score += 5;
      reasons.push('County matches');
    } else if (input.county && client.county) {
      missed.push(`County: "${input.county}" vs "${client.county}"`);
    }

    // Parcel ID match (strong signal)
    if (input.parcelId) {
      const clientCaseParcel = cases.find((c) => c.clientId === client.id && c.parcelId === input.parcelId);
      if (clientCaseParcel) {
        score += 40;
        reasons.push('Parcel ID matches');
      } else {
        missed.push('Parcel ID: no matching parcel on client cases');
      }
    }

    if (score < 20) continue; // Below threshold

    const clientCases = cases
      .filter((c) => c.clientId === client.id)
      .map((c) => ({
        caseId: c.caseId,
        event: c.program,
        status: c.status,
        tier: c.tier,
        damageLevel: c.housingCondition ?? null,
      }));

    // Cap at 100
    const normalizedScore = Math.min(score, 100);

    results.push({
      clientId: client.id,
      clientName: `${client.firstName} ${client.lastName}`,
      phone: client.phoneHome,
      address: client.primaryAddress?.address1 ?? null,
      county: client.county,
      score: normalizedScore,
      matchReasons: reasons,
      missedReasons: missed,
      cases: clientCases,
    });
  }

  return results.sort((a, b) => b.score - a.score);
}

/**
 * Compare iSTAT data against an existing case's housing fields.
 * Returns a list of fields where the values differ.
 */
export function compareIstatToCase(
  input: IstatMatchInput,
  tcrCase: TcrCase,
): IstatDiscrepancy[] {
  const discrepancies: IstatDiscrepancy[] = [];

  const comparisons: Array<{ field: string; label: string; istatValue: string; caseValue: string | null }> = [
    { field: 'housingCondition', label: 'Housing Condition', istatValue: input.damageLevel, caseValue: tcrCase.housingCondition ?? null },
    { field: 'waterDepth', label: 'Water Depth', istatValue: input.waterDepth, caseValue: tcrCase.waterDepth ?? null },
    { field: 'ownerRenter', label: 'Owner / Renter', istatValue: input.ownerOrRenter, caseValue: tcrCase.ownerRenter },
    { field: 'insuranceStatus', label: 'Insurance', istatValue: input.insuranceStatus, caseValue: tcrCase.insuranceEntries.length > 0 ? 'Yes' : 'No' },
  ];

  for (const c of comparisons) {
    const iVal = normalize(c.istatValue);
    const cVal = normalize(c.caseValue);
    if (!iVal || !cVal) continue; // skip if either side is empty
    if (iVal !== cVal) {
      discrepancies.push({
        field: c.field,
        label: c.label,
        caseValue: c.caseValue ?? '—',
        istatValue: c.istatValue,
        reviewed: false,
      });
    }
  }

  return discrepancies;
}
